import * as React from 'react';
import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../../../services/store';
import { IMedia } from '../../../types';
import Controls from './Controls';
import MediaContainer from './MediaContainer';
import { style } from 'typestyle';
import List from '../List';
import MediaProgress from './MediaProgress';
import useTapNavigation from '../../../hooks/useTapNavigation';
import useSwipeNavigation from '../../../hooks/useSwipeNavigation';
import NextIndicator from './NextIndicator';
import { productListZIndex } from '../../consts'; 

// Animation constants
const ANIMATION_DURATION_MS = 300;
const ANIMATION_DURATION_SEC = ANIMATION_DURATION_MS / 1000;

interface MobilePlayerProps {
  post: IMedia;
  settings: any;
  totalPosts: number;
  onClose: () => void;
  onPostChange: (post: IMedia | null) => void;
  classPrefix: string;
}

const MobilePlayer: FC<MobilePlayerProps> = ({
  post,
  settings,
  totalPosts,
  onClose,
  onPostChange,
  classPrefix,
}) => {
  const store = useStore();
  const [showList, setShowList] = useState(false);
  const [currentDisplayPost, setCurrentDisplayPost] = useState<IMedia>(post);
  const [nextDisplayPost, setNextDisplayPost] = useState<IMedia | null>(null);
  const [prevDisplayPost, setPrevDisplayPost] = useState<IMedia | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  
  const hasUserInteractedRef = useRef<boolean>(false);
  const medias = store.data.content.medias;
  const containerRef = useRef<HTMLDivElement>(null);

  // Preload next and previous posts when current post changes
  useEffect(() => {
    const currentIndex = currentDisplayPost.postIndex;
    
    if (currentIndex < medias.length - 1) {
      setNextDisplayPost(medias[currentIndex + 1]);
    } else {
      setNextDisplayPost(null);
    }
    
    if (currentIndex > 0) {
      setPrevDisplayPost(medias[currentIndex - 1]);
    } else {
      setPrevDisplayPost(null);
    }
  }, [currentDisplayPost, medias]);

  // Animate in on mount
  useEffect(() => {
    // Trigger mobilePlayerOpened event
    store.triggerEvent('mobilePlayerOpened', { post: currentDisplayPost }, store.data.id);
    
    // Small delay to ensure the DOM is ready
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  // Update displayed post when post prop changes
  useEffect(() => {
    if (post.id !== currentDisplayPost.id) {
      setCurrentDisplayPost(post);
      setIsPlaying(false); // Pause video when post changes
      
      hasUserInteractedRef.current = true;
    }
  }, [post]);

  // prevent background scrolling
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const toggleList = () => {
    setShowList(prev => !prev);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const togglePlay = () => {
    setIsPlaying(prev => !prev);
  };
  
  const handleClose = useCallback(() => {
    // Trigger mobilePlayerClosed event before closing
    store.triggerEvent('mobilePlayerClosed', { post: currentDisplayPost }, store.data.id);
    
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION_MS);
  }, [onClose, currentDisplayPost, store]);

  const handleNextPost = useCallback(() => {
    if (nextDisplayPost) {
      setCurrentDisplayPost(nextDisplayPost);
      onPostChange(nextDisplayPost);
      hasUserInteractedRef.current = true;
          store.triggerEvent('mobilePlayerNavigation', {
            direction: 'right',
            post: nextDisplayPost,
          }, store.data.id);
    } else {
      handleClose();
    }
  }, [nextDisplayPost, onPostChange, handleClose, store]);

  const handlePreviousPost = useCallback(() => {
    if (prevDisplayPost) {
      setCurrentDisplayPost(prevDisplayPost);
      onPostChange(prevDisplayPost);
      hasUserInteractedRef.current = true;
          store.triggerEvent('mobilePlayerNavigation', {
            direction: 'left',
            post: prevDisplayPost,
          }, store.data.id);
    } else {
      handleClose();
    }
  }, [prevDisplayPost, onPostChange, handleClose, store]);

  // Tap navigation and Swipe navigation use the same ref
  useTapNavigation({
    onNext: handleNextPost,
    onPrevious: handlePreviousPost,
    disabled: showList,
    containerRef, 
  });

  useSwipeNavigation({
    containerRef,
    onSwipeLeft: handleNextPost,
    onSwipeRight: handlePreviousPost,
    disabled: showList,
    threshold: 75
  });
  
  const showNextIndicator = !hasUserInteractedRef.current && nextDisplayPost !== null;

  // Get the background image URL for blurred background 
  const thumbnailImage = currentDisplayPost.type === 'video' && currentDisplayPost.thumbnail 
    ? currentDisplayPost.thumbnail 
    : currentDisplayPost.image;

  const animationClass = isClosing ? styles.closing : isVisible ? styles.visible : styles.hidden;

  const handleOverlayClick = () => {
    toggleList(); 
  };

  return (
    <div 
      ref={containerRef}
      className={`${classPrefix}-mobile-player ${styles.container} ${animationClass}`}
    >
      {/* Blurred background image*/}
      <div 
        className={styles.blurredBackground} 
        style={{ 
          backgroundImage: thumbnailImage ? `url(${thumbnailImage})` : 'none',
          backgroundColor: thumbnailImage ? 'transparent' : '#000000'
        }}
      />
      
      <div className={styles.contentContainer}>
        <MediaProgress 
          totalItems={medias.length} 
          currentIndex={currentDisplayPost.postIndex} 
        />

        <MediaContainer 
          currentDisplayPost={currentDisplayPost}
          nextDisplayPost={nextDisplayPost}
          prevDisplayPost={prevDisplayPost}
          currentYOffset={0}
          showList={showList}
          isMuted={isMuted}
          isPlaying={isPlaying}
          togglePlay={togglePlay}
        />
        
        <Controls
          post={currentDisplayPost}
          showList={showList}
          onClose={handleClose}
          toggleList={toggleList}
          isMuted={isMuted}
          isPlaying={isPlaying}
          toggleMute={toggleMute}
          togglePlay={togglePlay}
        />
        
        {showNextIndicator && <NextIndicator />}

        {showList && (
          <div 
            className={styles.overlay}
            onClick={handleOverlayClick}
          />
        )}

        {showList && (
          <List
            post={currentDisplayPost}
            settings={settings}
            totalPosts={totalPosts}
            onClose={toggleList}
            onPostChange={onPostChange}
            classPrefix={classPrefix}
          />
        )}
      </div>
    </div>
  );
};

export default MobilePlayer;

const styles = {
  container: style({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 9999,
    overflow: 'hidden',
    transition: `transform ${ANIMATION_DURATION_SEC}s ease-in-out`,
  }),
  // Animation classes
  hidden: style({
    transform: 'translateY(100%)',
  }),
  visible: style({
    transform: 'translateY(0)',
  }),
  closing: style({
    transform: 'translateY(100%)',
  }),
  contentContainer: style({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  }),
  blurredBackground: style({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'blur(20px)',
    transform: 'scale(1.2)', 
    zIndex: 1,
  }),
  overlay: style({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', 
    zIndex: productListZIndex - 1,
    cursor: 'pointer',
  }),
}
